import { useEffect, useRef, useState } from 'react';

export default function Cursor() {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const labelRef = useRef(null);
  const mouse = useRef({ x: 0, y: 0 });
  const ring = useRef({ x: 0, y: 0 });
  const [label, setLabel] = useState('');
  const [hovered, setHovered] = useState(false);
  const rafRef = useRef(null);

  useEffect(() => {
    // Hide on touch devices
    if ('ontouchstart' in window) return;

    const dot = dotRef.current;
    const ringEl = ringRef.current;
    if (!dot || !ringEl) return;

    document.body.style.cursor = 'none';

    const onMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
      dot.style.transform = `translate(${e.clientX - 4}px, ${e.clientY - 4}px)`;
    };

    const onOver = (e) => {
      const el = e.target.closest('a, button, [data-cursor]');
      if (el) {
        setHovered(true);
        const cursorType = el.getAttribute('data-cursor');
        if (cursorType === 'explore') setLabel('EXPLORE');
        else if (cursorType === 'drag') setLabel('DRAG');
        else if (el.href && el.href.startsWith('tel:')) setLabel('CALL');
        else setLabel('VIEW');
      }
    };

    const onOut = (e) => {
      const el = e.target.closest('a, button, [data-cursor]');
      if (el) {
        setHovered(false);
        setLabel('');
      }
    };

    const tick = () => {
      ring.current.x += (mouse.current.x - ring.current.x) * 0.07;
      ring.current.y += (mouse.current.y - ring.current.y) * 0.07;
      ringEl.style.transform = `translate(${ring.current.x - 20}px, ${ring.current.y - 20}px)`;
      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    document.addEventListener('mouseover', onOver, { passive: true });
    document.addEventListener('mouseout', onOut, { passive: true });
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.body.style.cursor = '';
      window.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseover', onOver);
      document.removeEventListener('mouseout', onOut);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, []);

  // Hide on touch devices
  if (typeof window !== 'undefined' && 'ontouchstart' in window) return null;

  return (
    <>
      {/* Inner dot */}
      <div
        ref={dotRef}
        className="fixed top-0 left-0 z-[9999] pointer-events-none"
        style={{
          width: 8, height: 8, borderRadius: '50%',
          background: '#E8A020',
          transition: hovered ? 'transform 0.15s, opacity 0.15s' : 'none',
          opacity: hovered ? 0 : 1,
          transform: 'translate(-100px, -100px)',
          mixBlendMode: 'difference',
        }}
      />
      {/* Outer ring */}
      <div
        ref={ringRef}
        className="fixed top-0 left-0 z-[9998] pointer-events-none flex items-center justify-center"
        style={{
          width: 40, height: 40, borderRadius: '50%',
          border: '1.5px solid #E8A020',
          opacity: 0.5,
          transition: 'width 0.3s, height 0.3s, border-radius 0.3s, background 0.3s, opacity 0.3s',
          ...(hovered ? {
            width: 80, height: 80,
            marginLeft: -20, marginTop: -20,
            background: 'rgba(232,160,32,0.06)',
            opacity: 0.9,
          } : {}),
          transform: 'translate(-100px, -100px)',
          mixBlendMode: 'difference',
        }}
      >
        {label && (
          <span
            ref={labelRef}
            className="font-sans font-bold text-[9px] tracking-[0.08em] uppercase"
            style={{ color: '#E8A020' }}
          >
            {label}
          </span>
        )}
      </div>
    </>
  );
}
